import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { Avatar, Button, CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import { useRef, useState } from "react";
import { FaRegEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import CommentAttachments from "./CommentAttachments";
import {
  useCreateSubtaskNote,
  useDeleteSubtaskNote,
  useSubtaskNotes,
  useUpdateSubtaskNote,
} from "utils/taskService";
import { useAttachmentUploads } from "hooks/useAttachmentUploads";
import { handleAttachmentPaste } from "utils/attachmentClipboard";
import { formatRelativeTime, getAvatarColor } from "utils/taskUtils";
import { isOrgSuperAdmin } from "utils/permissionUtils";

export const MAX_NOTE_ATTACHMENTS = 10;

/**
 * Running notes on one subtask. Kept separate from SubtaskManager because notes only exist
 * for a persisted subtask — a draft subtask has no id to hang them off yet.
 */
const SubtaskNotes = ({ taskId, subtaskId, compact = false }) => {
  const [draft, setDraft] = useState("");
  const [draftAttachments, setDraftAttachments] = useState([]);
  const [editing, setEditing] = useState({ id: null, body: "", attachments: [] });
  const [notePendingDeletion, setNotePendingDeletion] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const { isUploading, uploadFiles, addLinks } = useAttachmentUploads({
    max: MAX_NOTE_ATTACHMENTS,
    noun: "note",
  });

  const canOverseeOrg = isOrgSuperAdmin();

  const currentUserId = (() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail"))?.id ?? null;
    } catch {
      return null;
    }
  })();

  const { data: notes = [], isLoading } = useSubtaskNotes(taskId, subtaskId);
  const { mutate: createNote, isLoading: isPosting } = useCreateSubtaskNote({
    onSuccess: () => {
      setDraft("");
      setDraftAttachments([]);
    },
    onError: () => toast.error("Could not add the note"),
  });
  const { mutate: updateNote } = useUpdateSubtaskNote({
    onSuccess: () => setEditing({ id: null, body: "", attachments: [] }),
    onError: () => toast.error("Could not update the note"),
  });
  const { mutate: deleteNote } = useDeleteSubtaskNote({
    onSuccess: () => setNotePendingDeletion(null),
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Could not delete the note");
      setNotePendingDeletion(null);
    },
  });

  const appendToDraft = (added) => setDraftAttachments((prev) => [...prev, ...added]);
  const appendToEdit = (added) =>
    setEditing((prev) => ({ ...prev, attachments: [...prev.attachments, ...added] }));

  /** Files are uploaded immediately; the note only ever carries the resulting links. */
  const handleFilesSelected = (event, current, append) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    uploadFiles(files, current, append);
  };

  const pasteInto = (current, append) => (event) => {
    if (isUploading) return;
    handleAttachmentPaste(event, {
      onFiles: (files) => uploadFiles(files, current, append),
      onLinks: (links) => addLinks(links, current, append),
    });
  };

  const handlePost = () => {
    const body = draft.trim();
    if (!body && !draftAttachments.length) return;
    createNote({ id: taskId, subtaskId, body, attachments: draftAttachments });
  };

  const handleSaveEdit = () => {
    const body = editing.body.trim();
    if (!body && !editing.attachments.length) return;
    updateNote({
      id: taskId,
      subtaskId,
      noteId: editing.id,
      body,
      attachments: editing.attachments,
    });
  };

  return (
    <div className="mt-2 rounded-lg border border-app-border bg-app-surface-muted p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-app-text-muted mb-2">
        Notes {notes.length > 0 && `(${notes.length})`}
      </p>

      {isLoading ? (
        <p className="text-xs text-app-text-muted">Loading notes…</p>
      ) : notes.length === 0 ? (
        <p className="text-xs text-app-text-muted">
          No notes yet — add context, blockers, or anything worth remembering.
        </p>
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => (
            <li key={note.id} className="flex items-start gap-2">
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 12,
                  bgcolor: getAvatarColor(note.authorId),
                }}
              >
                {(note.authorName || "?").charAt(0).toUpperCase()}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-app-text">
                    {note.authorName || "Unknown"}
                  </span>
                  <span className="text-xs text-app-text-muted">
                    {formatRelativeTime(note.createdDate)}
                  </span>
                  {(() => {
                    // A note is its author's to reword; a super admin may only remove one.
                    const isAuthor = note.authorId === currentUserId;
                    if (!isAuthor && !canOverseeOrg) return null;
                    return (
                      <span className="ml-auto flex items-center">
                        {isAuthor && (
                          <Tooltip title="Edit note">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditing({
                                  id: note.id,
                                  body: note.body,
                                  attachments: note.attachments || [],
                                })
                              }
                            >
                              <FaRegEdit size={12} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={isAuthor ? "Delete note" : "Delete note (super admin)"}>
                          <IconButton size="small" onClick={() => setNotePendingDeletion(note)}>
                            <FaTrash size={12} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    );
                  })()}
                </div>

                {editing.id === note.id ? (
                  <div className="mt-1 space-y-1">
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={editing.body}
                      onChange={(e) => setEditing((prev) => ({ ...prev, body: e.target.value }))}
                      onPaste={pasteInto(editing.attachments, appendToEdit)}
                    />
                    <CommentAttachments
                      attachments={editing.attachments}
                      onRemove={(i) =>
                        setEditing((prev) => ({
                          ...prev,
                          attachments: prev.attachments.filter((_, index) => index !== i),
                        }))
                      }
                    />
                    <div className="flex gap-2">
                      <Button size="small" variant="contained" onClick={handleSaveEdit}>
                        Save
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={isUploading}
                        startIcon={<AttachFileOutlinedIcon fontSize="small" />}
                        onClick={() => editFileInputRef.current?.click()}
                        sx={{ textTransform: "none" }}
                      >
                        Attach
                      </Button>
                      <input
                        ref={editFileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => handleFilesSelected(e, editing.attachments, appendToEdit)}
                      />
                      <Button
                        size="small"
                        onClick={() => setEditing({ id: null, body: "", attachments: [] })}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {note.body && (
                      <p className="text-sm text-app-text whitespace-pre-wrap break-words">
                        {note.body}
                      </p>
                    )}
                    <CommentAttachments attachments={note.attachments || []} />
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3">
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={compact ? 1 : 2}
          placeholder="Add a note… or paste files to attach them"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPaste={pasteInto(draftAttachments, appendToDraft)}
        />

        <CommentAttachments
          attachments={draftAttachments}
          onRemove={(i) => setDraftAttachments(draftAttachments.filter((_, index) => index !== i))}
        />

        <div className="mt-2 flex items-center justify-between gap-2">
          <Button
            size="small"
            color="inherit"
            disabled={isUploading}
            startIcon={
              isUploading ? (
                <CircularProgress size={14} />
              ) : (
                <AttachFileOutlinedIcon fontSize="small" />
              )
            }
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: "none" }}
          >
            {isUploading ? "Uploading..." : "Attach"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => handleFilesSelected(e, draftAttachments, appendToDraft)}
          />
          <Button
            size="small"
            variant="contained"
            disabled={(!draft.trim() && !draftAttachments.length) || isPosting || isUploading}
            onClick={handlePost}
          >
            Post
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={notePendingDeletion != null}
        title="Delete note?"
        message="This note will be removed from the subtask. This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => deleteNote({ id: taskId, subtaskId, noteId: notePendingDeletion?.id })}
        onCancel={() => setNotePendingDeletion(null)}
      />
    </div>
  );
};

export default SubtaskNotes;
